// Section 22 event prompt (stream E2): every open event prompt of the season with its text, the
// choices as buttons with a one-line consequence preview under each, and what has been chosen;
// for a group at the gate, the arrival screen (Section 14): the candidates, Admit all, Admit some
// (tick boxes), Admit as guests, Turn away, and the migration policy that can decide for you.
// The overlay root reads the store (game, draft, status, ui.promptId) and dispatches answerEvent;
// everything below it takes props. Keyboard: Tab moves between choices, Enter or Space selects;
// the chosen button is marked in words ("Chosen"), not by colour alone.
import { useEffect, useRef, useState } from 'preact/hooks';
import type {
  JobId,
  MigrationOption,
  Narration,
  PersonId,
  PromptAnswer,
  PromptId,
} from '../../sim/api';
import {
  answerEvent,
  closeOverlay,
  draft,
  game,
  runConfig,
  setMigrationPolicy,
  status,
  ui,
} from '../../store';
import { jobLabel } from '../../viewModels';
import { eventsVM, MIGRATION_OPTIONS, type EventPromptVM } from '../../viewModels/events';
import { eventsText as t, fill } from '../text/en';
import { ArrivalTable } from './arrival-table';
import './events.css';

const say = (n: Narration): string => fill(n, jobLabel);

interface CardProps {
  vm: EventPromptVM;
  active: boolean;
  disabled: boolean;
  migration: MigrationOption;
  jobLabel: (job: JobId) => string;
  onAnswer: (promptId: PromptId, answer: PromptAnswer) => void;
  onMigration: (option: MigrationOption) => void;
}

export function PromptCard({
  vm,
  active,
  disabled,
  migration,
  jobLabel: label,
  onAnswer,
  onMigration,
}: CardProps) {
  const [picked, setPicked] = useState<PersonId[]>(
    vm.answer?.choiceId === 'admit-some' ? vm.answer.personIds : [],
  );
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (active) ref.current?.scrollIntoView({ block: 'nearest' });
  }, [active]);
  const titleId = `event-${vm.promptId.replace(/[^a-z0-9-]/gi, '-')}`;
  const pickable = vm.choices.some((c) => c.picksPeople);
  const toggle = (id: PersonId): void => {
    setPicked((now) => (now.includes(id) ? now.filter((x) => x !== id) : [...now, id].sort()));
  };
  const chosen = vm.choices.find((c) => c.id === vm.answer?.choiceId);
  return (
    <article ref={ref} class={`events-card${active ? ' is-active' : ''}`} aria-labelledby={titleId}>
      <h3 id={titleId} class="events-card__text">
        {say(vm.text)}
      </h3>
      <p class="events-card__status">
        {chosen === undefined ? t.notYet : `${t.answered}: ${say(chosen.label)}`}
      </p>
      {vm.groupId !== null && (
        <>
          <ArrivalTable
            candidates={vm.candidates}
            picked={picked}
            pickable={pickable}
            disabled={disabled}
            followUp={vm.followUp}
            jobLabel={label}
            onToggle={toggle}
          />
          {pickable && <p class="hint">{t.pickSome(picked.length)}</p>}
        </>
      )}
      <ul class="events-choices">
        {vm.choices.map((c) => {
          const isChosen = vm.answer?.choiceId === c.id;
          const needsPick = c.picksPeople && picked.length === 0;
          return (
            <li key={c.id}>
              <button
                type="button"
                class={`button events-choice${isChosen ? ' is-chosen' : ''}`}
                aria-pressed={isChosen}
                aria-describedby={`${titleId}-${c.id}`}
                disabled={disabled || needsPick}
                onClick={() =>
                  onAnswer(vm.promptId, {
                    kind: 'event',
                    choiceId: c.id,
                    personIds: c.picksPeople ? picked : [],
                  })
                }
              >
                {isChosen && <span aria-hidden="true">✓ </span>}
                {say(c.label)}
                {isChosen && <span class="visually-hidden"> ({t.answered})</span>}
              </button>
              <div id={`${titleId}-${c.id}`} class="events-preview">
                {needsPick && <span>{t.pickNone} </span>}
                {c.previews.map((p, i) => (
                  <span key={i}>{say(p)} </span>
                ))}
              </div>
            </li>
          );
        })}
      </ul>
      {vm.groupId !== null && !vm.followUp && (
        <p class="events-policy">
          <label>
            {t.migration}{' '}
            <select
              value={migration}
              disabled={disabled}
              onChange={(e) =>
                onMigration((e.target as HTMLSelectElement).value as MigrationOption)
              }
            >
              {MIGRATION_OPTIONS.map((o) => (
                <option key={o} value={o}>
                  {t.migrationOption[o]}
                </option>
              ))}
            </select>
          </label>{' '}
          <span class="hint">{t.migrationHint}</span>
        </p>
      )}
    </article>
  );
}

/** The overlay root (mounted by the overlay host for ui.overlay 'event', Section 22). */
export function EventPrompt() {
  const g = game.value;
  if (g === null) return null;
  const vm = eventsVM(g, draft.value, runConfig());
  const disabled = status.value !== 'deciding';
  const active = ui.value.promptId;
  return (
    <section class="events" aria-labelledby="events-title">
      <h2 id="events-title">{t.title}</h2>
      <p class="hint">
        {vm.total === 0 ? t.none : t.progress(vm.answered, vm.total)}
        {vm.gatesClosedUntilTurn !== null && vm.gatesClosedUntilTurn >= g.turn && (
          <> {t.gatesClosed(vm.gatesClosedUntilTurn)}</>
        )}
      </p>
      {disabled && <p class="hint">{t.readOnly}</p>}
      {vm.prompts.map((p) => (
        <PromptCard
          key={p.promptId}
          vm={p}
          active={p.promptId === active}
          disabled={disabled}
          migration={vm.migration}
          jobLabel={jobLabel}
          onAnswer={(id, answer) => answerEvent(id, answer)}
          onMigration={(option) => setMigrationPolicy(option)}
        />
      ))}
      <footer class="overlay__actions">
        <button type="button" class="button button--primary" onClick={() => closeOverlay()}>
          {t.done}
        </button>
      </footer>
    </section>
  );
}
